/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import type { ShopperSearch } from '@/scapi';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { getCurrencySymbol } from '@/lib/currency';
import { cn } from '@/lib/utils';
import {
    formatPriceRefineValue,
    getPriceSliderBounds,
    parsePriceRangeFromRefines,
    PRICE_FACET_ATTRIBUTE_ID,
} from '@/components/eataly-plp-filters/lib/price-facet';

export type EatalyPlpPriceFilterPanelProps = {
    refinements?: ShopperSearch.schemas['ProductSearchResult']['refinements'];
    refine: string[];
    onApply: (rangeString: string | null) => void;
    onClose: () => void;
    /** Reset draft when the panel opens. */
    open: boolean;
    className?: string;
};

export default function EatalyPlpPriceFilterPanel({
    refinements,
    refine,
    onApply,
    onClose,
    open,
    className,
}: EatalyPlpPriceFilterPanelProps): ReactElement {
    const { t, i18n } = useTranslation('categoryPlp');
    const { currency } = useSite();
    const currencySymbol = getCurrencySymbol(i18n.language, currency);

    const priceValues = useMemo(() => {
        const price = refinements?.find((r) => r.attributeId === PRICE_FACET_ATTRIBUTE_ID);
        return price?.values;
    }, [refinements]);

    const { absoluteMin, absoluteMax } = useMemo(() => getPriceSliderBounds(priceValues), [priceValues]);

    const appliedRange = useMemo(() => parsePriceRangeFromRefines(refine), [refine]);

    const [draftRange, setDraftRange] = useState<[number, number]>(() =>
        appliedRange ? [appliedRange.min, appliedRange.max] : [absoluteMin, absoluteMax]
    );

    useEffect(() => {
        if (!open) {
            return;
        }
        setDraftRange(appliedRange ? [appliedRange.min, appliedRange.max] : [absoluteMin, absoluteMax]);
    }, [open, appliedRange, absoluteMin, absoluteMax]);

    const handleSliderChange = useCallback((newValues: number[]) => {
        setDraftRange([newValues[0], newValues[1]]);
    }, []);

    const handleReset = useCallback(() => {
        setDraftRange([absoluteMin, absoluteMax]);
        onApply(null);
        onClose();
    }, [absoluteMin, absoluteMax, onApply, onClose]);

    const handleApply = useCallback(() => {
        onApply(formatPriceRefineValue(draftRange[0], draftRange[1]));
        onClose();
    }, [draftRange, onApply, onClose]);

    return (
        <div data-testid="plp-price-filter-panel" className={cn('relative flex w-full flex-col gap-6', className)}>
            <div className="flex flex-col gap-4 pt-2">
                <div className="px-0.5 py-3" onPointerDown={(e) => e.stopPropagation()}>
                    <Slider
                        value={draftRange}
                        onValueChange={handleSliderChange}
                        min={absoluteMin}
                        max={absoluteMax}
                        step={1}
                        minStepsBetweenThumbs={1}
                        data-testid="plp-price-filter-slider"
                    />
                </div>

                <div className="flex items-center justify-center gap-2 body-copy-l text-neutral-10">
                    <p>
                        {draftRange[0]} {currencySymbol}
                    </p>
                    <p>-</p>
                    <p>
                        {draftRange[1]} {currencySymbol}
                    </p>
                </div>
            </div>

            <div className="flex w-full gap-2">
                <Button
                    type="button"
                    hierarchy="secondary"
                    mode="light"
                    size="large"
                    className="flex-1 cursor-pointer uppercase"
                    onClick={handleReset}
                    data-testid="plp-price-filter-reset">
                    {t('priceFilter.reset')}
                </Button>
                <Button
                    type="button"
                    hierarchy="primary"
                    mode="light"
                    size="large"
                    className="flex-1 cursor-pointer uppercase"
                    onClick={handleApply}
                    data-testid="plp-price-filter-apply">
                    {t('priceFilter.apply')}
                </Button>
            </div>
        </div>
    );
}
